import { ChevronIcon, OtherPanel } from './WalletShared';
import { COLORS, CONTRACT_ITEMS, TOKEN_ICON_IMAGES, UO_ASSET_STATS, WALLET_COLORS as C } from '@/constants';
import { formatWalletBalance } from '@/utils/wallet-display';
import { isPerfCellKey } from '@/utils/wallet-daily-perf';
import type { UoAssetStaticDisplay } from '@/utils/static-reward-display';

type Translate = (key: string) => string;
type BalanceCell = { key: string; label: string };

function fmtPerfValue(raw: string): string {
  const n = fmtBalInner(raw);
  return n === '0' ? '0$' : `${n}$`;
}

function fmtBalInner(raw: string): string {
  return formatWalletBalance(raw);
}

export function AssetOverview({
  t,
  open,
  onToggle,
  cells,
  fmtBal,
  onPerfCellClick,
}: {
  t: Translate;
  open: boolean;
  onToggle: () => void;
  cells: BalanceCell[];
  fmtBal: (key: string) => string;
  onPerfCellClick?: (period: 'today' | 'yesterday') => void;
}) {
  return (
    <OtherPanel className="px-1.5">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between p-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <img src="/brand/icon-uo-token.png" alt="" className="size-[25px]" draggable={false} />
          {t('wallet.assetOverview')}
        </div>
        <ChevronIcon up={open} />
      </button>
      {open && (
        <div className="grid grid-cols-3 gap-0.5 pb-3">
          {cells.map(({ key, label }) => {
            const icon = TOKEN_ICON_IMAGES[key] ?? '/brand/icon-uo-token.png';
            const clickable = isPerfCellKey(key) && !!onPerfCellClick;
            const value = isPerfCellKey(key) ? fmtPerfValue(fmtBal(key)) : fmtBal(key);
            const inner = (
              <>
                <img src={icon} alt="" className="size-6 shrink-0 object-contain" draggable={false} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div
                    className="w-full truncate text-sm font-semibold leading-none"
                    style={{ color: isPerfCellKey(key) ? C.gold : COLORS.iconGlobal }}
                  >
                    {value}
                  </div>
                  <div className="w-full truncate text-[9px] leading-none" style={{ color: COLORS.iconColor }}>
                    {label}
                    {clickable ? ' ›' : ''}
                  </div>
                </div>
              </>
            );
            if (clickable) {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onPerfCellClick(key === 'today_perf' ? 'today' : 'yesterday')}
                  className="flex min-h-[45px] w-full items-center gap-1 border p-1.5 text-left transition active:scale-[0.98]"
                  style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}
                >
                  {inner}
                </button>
              );
            }
            return (
              <div key={key} className="flex min-h-[45px] items-center gap-1 border p-1.5" style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
    </OtherPanel>
  );
}

export function ContractPanel({ t, open, onToggle, balances }: { t: Translate; open: boolean; onToggle: () => void; balances: Record<string, string> }) {
  return (
    <OtherPanel className="px-1.5">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between p-2.5">
        <span className="flex items-center gap-2 text-sm font-semibold text-white">
          <img src="/brand/icon-uo-contract.png" alt="" className="size-[25px] object-contain" draggable={false} />
          {t('wallet.uoContract')}
        </span>
        <ChevronIcon up={open} />
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-0.5 pb-3">
          {CONTRACT_ITEMS.map(({ labelKey, key, fallback }, index, items) => (
            <div key={key} className={`flex min-h-[60px] flex-col items-center justify-center gap-2 border px-1 py-3 ${index === items.length - 1 ? 'col-span-2' : ''}`} style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}>
              <div className="truncate text-base font-semibold leading-none" style={{ color: COLORS.iconGlobal }}>{formatWalletBalance(balances[key] ?? fallback)}</div>
              <div className="truncate text-[9px] leading-none" style={{ color: COLORS.iconColor }}>{t(labelKey)}</div>
            </div>
          ))}
        </div>
      )}
    </OtherPanel>
  );
}

export function UoAssetPanel({
  t,
  open,
  onToggle,
  staticDisplay,
  loading,
}: {
  t: Translate;
  open: boolean;
  onToggle: () => void;
  staticDisplay: UoAssetStaticDisplay;
  loading?: boolean;
}) {
  const statValues = [
    staticDisplay.packageLabel,
    staticDisplay.qtyLabel,
    staticDisplay.forkLabel,
  ];

  return (
    <OtherPanel className="px-1.5">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between p-2.5">
        <span className="flex items-center gap-2 text-sm font-semibold text-white">
          <img src="/brand/icon-uo-assets.png" alt="" className="size-[25px] object-contain" draggable={false} />
          {t('wallet.uoAsset')}
        </span>
        <ChevronIcon up={open} />
      </button>
      {open && (
        <div className="pb-3">
          <div className="grid grid-cols-3 gap-0.5">
            {UO_ASSET_STATS.map(({ labelKey }, index) => (
              <div key={labelKey} className="flex min-h-[52px] flex-col items-center justify-center gap-1.5 border px-1.5 py-3 text-center" style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}>
                <div className="truncate text-sm font-semibold leading-none" style={{ color: COLORS.iconGlobal }}>
                  {loading ? '…' : statValues[index]}
                </div>
                <div className="truncate text-[9px] leading-none" style={{ color: COLORS.iconColor }}>{t(labelKey)}</div>
              </div>
            ))}
          </div>
          <div className="mt-0.5 px-3 py-3 border" style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="text-[10px]" style={{ color: COLORS.iconColor }}>{t('wallet.staticValue')}</span>
              <span className="truncate font-mono text-xs font-semibold" style={{ color: COLORS.iconGlobal }}>
                {loading
                  ? '…'
                  : `${staticDisplay.paidLabel} / ${staticDisplay.capLabel} / ${t('wallet.exit')}`}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-black/45 shadow-[0_1px_4px_rgba(0,0,0,0.45)_inset]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#A7E768] via-[#F5EC63] to-[#FBDD65] shadow-[0_0_10px_rgba(245,236,99,0.65)] transition-[width] duration-300"
                style={{ width: loading ? '0%' : `${staticDisplay.progressPct}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </OtherPanel>
  );
}

